import { merchantsPayoutStatusQueryDocument } from 'APIs/graphql';
import { MerchantPayoutStatus } from 'APIs/graphql/generated/graphql';
import { updateMerchantPayoutStatus } from 'APIs/merchants/merchants';
import { PayoutStatusUpdateRequest } from 'APIs/merchants/merchants.types';
import { FunctionComponent, MouseEvent, useCallback, useMemo, useState } from 'react';
import { MdRefresh } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useQuery } from 'urql';
import getErrorMessage from 'utils/getErrorMessage';

import MerchantsDataTable from './MerchantsDataTable';
import { PayoutsDisabledMerchantsHeader, RefreshButton } from './styles';
import { IListingPagePropsObj } from './types';

const initListingPageProps = { first: 20, last: null, before: null, after: null, page: 1 };
const LISTING_RECORDS_PER_PAGE = 20;

const FailedPayouts = () => {
  const RefreshIcon = MdRefresh as FunctionComponent;

  const { apiBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
  }));

  const [listingPageProps, setListingPagePropsObj] = useState<IListingPagePropsObj>(initListingPageProps);

  const { first, last, before, after } = useMemo(() => listingPageProps, [listingPageProps]);

  const limit = useMemo(() => first ?? last ?? LISTING_RECORDS_PER_PAGE, [first, last]);

  const filters = useMemo(() => {
    return {
      payoutStatus: 'Disabled' as MerchantPayoutStatus,
    };
  }, []);

  const [{ fetching, data, error: payoutsError }, reexecuteQuery] = useQuery({
    query: merchantsPayoutStatusQueryDocument,
    variables: { last, before, first, after, ...filters },
  });

  const handlePageChange = useCallback(
    (page: number) => {
      if (!data?.merchants) return;

      const isFirst = page === 1;
      const isPrev = !isFirst && page === listingPageProps.page - 1;
      const isNext = page === listingPageProps.page + 1;
      const isLast = !isFirst && !isNext && page === Math.ceil(data.merchants.totalCount / limit);

      setListingPagePropsObj({
        ...listingPageProps,
        page,
        last: isLast || isPrev ? limit : null,
        before: !isLast && isPrev ? data.merchants.pageInfo.startCursor ?? null : null,
        first: isFirst || isNext ? limit : null,
        after: !isFirst && isNext ? data.merchants.pageInfo.endCursor ?? null : null,
      });
    },
    [data?.merchants, limit, listingPageProps],
  );

  const handlePerRowsChange = useCallback(
    async (newPerPage: number, page: number) => {
      setListingPagePropsObj({
        ...listingPageProps,
        page,
        last: listingPageProps.last !== null ? newPerPage : null,
        first: listingPageProps.first !== null ? newPerPage : null,
      });
    },
    [listingPageProps],
  );

  const enablePayoutsAction = useCallback(
    async (merchantId: string) => {
      if (!merchantId) return;

      if (window.confirm(`Are you sure you want to enable payouts for merchant: ${merchantId}`)) {
        try {
          const payoutStatusUpdateRequest: PayoutStatusUpdateRequest = {
            payoutStatus: 'Enabled',
          };
          await updateMerchantPayoutStatus(apiBaseUri, merchantId, payoutStatusUpdateRequest);
          reexecuteQuery({ requestPolicy: 'network-only' });
        } catch (e) {
          console.log(getErrorMessage(e));
        }
      }
    },
    [apiBaseUri, reexecuteQuery],
  );

  const handleRefreshClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      reexecuteQuery({ requestPolicy: 'network-only' });
    },
    [reexecuteQuery],
  );

  return (
    <div style={{ width: '100%' }}>
      <PayoutsDisabledMerchantsHeader>
        <h3>Merchants with payouts disabled</h3>
        <RefreshButton onClick={handleRefreshClick}>
          Refresh <RefreshIcon />
        </RefreshButton>
      </PayoutsDisabledMerchantsHeader>
      <MerchantsDataTable
        merchantsList={data?.merchants?.edges}
        totalRows={data?.merchants?.totalCount ?? 0}
        handlePageChange={handlePageChange}
        handlePerRowsChange={handlePerRowsChange}
        fetchListLoader={fetching}
        limit={limit}
        isErrorRequest={payoutsError !== undefined}
        enablePayouts={enablePayoutsAction}
      />
    </div>
  );
};

export default FailedPayouts;
